import React, { useEffect, useState } from "react";
import { PASSWORD_PATTERN } from "../../../utils/credentialGateUtils";
import {
  CredentialAccessCodeRequirements,
  CredentialModalTabs,
  CredentialPasswordField,
  CredentialPasswordRequirements,
} from "./credentialChangeModalParts";
import "./ChangeCredentialsModal.scss";

const AdminChangeUserCredentialsModal = ({
  open,
  targetUser,
  onClose,
  onSubmit,
}) => {
  const [tab, setTab] = useState("password");
  const [error, setError] = useState("");
  const [passwordForm, setPasswordForm] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [accessCodeForm, setAccessCodeForm] = useState({
    newAccessCode: "",
    confirmAccessCode: "",
  });

  useEffect(() => {
    if (!open) return;
    setTab("password");
    setError("");
    setPasswordForm({ newPassword: "", confirmPassword: "" });
    setAccessCodeForm({ newAccessCode: "", confirmAccessCode: "" });
  }, [open, targetUser?.StaffId]);

  if (!open || !targetUser) return null;

  const userLabel = targetUser.Name || targetUser.EmailId || "this user";
  const isPassword = tab === "password";

  const handleTabChange = (nextTab) => {
    setTab(nextTab);
    setError("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");

    if (isPassword) {
      const next = passwordForm.newPassword.trim();
      const confirm = passwordForm.confirmPassword.trim();
      if (!PASSWORD_PATTERN.test(next)) {
        setError("Please meet all password requirements before continuing.");
        return;
      }
      if (next !== confirm) {
        setError("New password and confirm password do not match.");
        return;
      }
      onSubmit({ mode: "password", value: next });
      return;
    }

    const next = accessCodeForm.newAccessCode.trim();
    const confirm = accessCodeForm.confirmAccessCode.trim();
    if (next.length !== 6 || !/^\d{6}$/.test(next)) {
      setError("Please meet all access code requirements before continuing.");
      return;
    }
    if (next !== confirm) {
      setError("New access code and confirm access code do not match.");
      return;
    }
    onSubmit({ mode: "accessCode", value: next });
  };

  return (
    <div
      className="modal fade show d-block credential-admin-modal"
      tabIndex="-1"
      role="dialog"
      aria-modal="true"
      style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1200 }}
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title mb-0">Change credentials</h5>
            <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <p className="text-muted small mb-3">
                For <strong>{userLabel}</strong>
              </p>

              <CredentialModalTabs tab={tab} onTabChange={handleTabChange} />

              {error && (
                <div className="alert alert-danger py-2 px-3 small mb-3" role="alert">
                  {error}
                </div>
              )}

              {isPassword ? (
                <>
                  <CredentialPasswordRequirements password={passwordForm.newPassword} />
                  <CredentialPasswordField
                    id="admin-new-password"
                    label="New password"
                    value={passwordForm.newPassword}
                    onChange={(e) =>
                      setPasswordForm((p) => ({ ...p, newPassword: e.target.value }))
                    }
                  />
                  <CredentialPasswordField
                    id="admin-confirm-password"
                    label="Confirm password"
                    value={passwordForm.confirmPassword}
                    onChange={(e) =>
                      setPasswordForm((p) => ({
                        ...p,
                        confirmPassword: e.target.value,
                      }))
                    }
                  />
                </>
              ) : (
                <>
                  <CredentialAccessCodeRequirements
                    newAccessCode={accessCodeForm.newAccessCode}
                    confirmAccessCode={accessCodeForm.confirmAccessCode}
                  />
                  <CredentialPasswordField
                    id="admin-new-access-code"
                    label="New access code"
                    value={accessCodeForm.newAccessCode}
                    maxLength={6}
                    onChange={(e) =>
                      setAccessCodeForm((p) => ({
                        ...p,
                        newAccessCode: e.target.value.replace(/\D/g, "").slice(0, 6),
                      }))
                    }
                  />
                  <CredentialPasswordField
                    id="admin-confirm-access-code"
                    label="Confirm access code"
                    value={accessCodeForm.confirmAccessCode}
                    maxLength={6}
                    onChange={(e) =>
                      setAccessCodeForm((p) => ({
                        ...p,
                        confirmAccessCode: e.target.value.replace(/\D/g, "").slice(0, 6),
                      }))
                    }
                  />
                </>
              )}
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                <i className="fas fa-times me-1" aria-hidden="true" />
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                <i className="fas fa-check me-1" aria-hidden="true" />
                Continue
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminChangeUserCredentialsModal;
